import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { MiBuildHouseAlt } from "@miplatform/assets";
import {
  MiButton,
  MiModalConfirm,
  MiPlaceholderArea,
  MiSkeleton,
  SKELETON_VARIANTS,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { BidLineItemType } from "@miplatform/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IBidLineItemPayload } from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { BidLineItemCard, BidLineItemForm } from "../../components";
import { customerLineItemsTabsSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerLineItemsTabsViewModel } from "./useCustomerLineItemsTabsViewModel";

export const DEFAULT_VALUES: IBidLineItemPayload = {
  accountId: "",
  name: "",
  footnote: "",
  categoryIds: [],
  bidLineItemType: BidLineItemType.Option,
};

export function CustomerOptLineItemsTab() {
  const { goBack } = useRouter();
  const { accountDetails, setSaveHandler, setCancelHandler } =
    useCustomerPricingDetailsContext();

  const theme = useTheme();

  const form = useForm<IBidLineItemPayload>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(customerLineItemsTabsSchema),
  });
  const { reset, getValues } = form;

  const {
    openLineItemForm,
    setOpenLineItemForm,
    categories,
    getCategories,
    bidLineItems,
    getBidLineItems,
    openDeleteModal,
    setOpenDeleteModal,
    selectedLineItem,
    setSelectedLineItem,
    handleCreateBidLineItem,
    handleUpdateBidLineItem,
    handleConfirmDeleteModal,
    isLoading,
  } = useCustomerLineItemsTabsViewModel({
    accountId: accountDetails?.id || "",
    bidLineItemType: BidLineItemType.Option,
    reset,
  });

  useTabFormRegistration(form, isLoading);

  useEffect(() => {
    getCategories();

    setSaveHandler(async () => {
      const data = getValues();
      if (data.id) {
        return handleUpdateBidLineItem(data.id, data);
      }
      return handleCreateBidLineItem(data);
    });
  }, []);

  useEffect(() => {
    if (accountDetails) {
      getBidLineItems(accountDetails.id, BidLineItemType.Option);
      reset({
        ...DEFAULT_VALUES,
        accountId: accountDetails?.id || "",
      });
    }
  }, [accountDetails]);

  useEffect(() => {
    if (openLineItemForm) {
      setCancelHandler(() => {
        setOpenLineItemForm(false);
        reset({
          ...DEFAULT_VALUES,
          accountId: accountDetails?.id || "",
        });
      });
    } else {
      setCancelHandler(goBack);
    }
  }, [openLineItemForm]);

  return (
    <>
      <ConfigAccordionHeader
        title="Option Line Items"
        disableCollapse
        customAction={
          <MiButton
            title="Add Option Line Item"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() => setOpenLineItemForm(true)}
            disabled={isLoading || openLineItemForm}
            size="small"
            component="div"
          />
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography
            variant={TYPOGRAPHY_VARIANTS.BODY2}
            color={theme.palette.text.secondary}
          >
            Create an Option Line Item to group categories so the pricing
            appears as a separate line on the bid.
          </Typography>
          <BidLineItemForm
            open={openLineItemForm}
            form={form}
            categories={categories}
            disabled={isLoading}
          />
          {isLoading && (
            <MiSkeleton height={450} variant={SKELETON_VARIANTS.ROUNDED} />
          )}
          {!isLoading && !openLineItemForm && bidLineItems.length === 0 && (
            <MiPlaceholderArea
              text="No Option Line Items Created"
              helperText="Tip: To create, click + Add Option Line Item."
              iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
              containerStyle={{
                backgroundColor: theme.palette.primary.light,
                height: "100%",
                minHeight: 450,
              }}
            />
          )}
          {!isLoading && bidLineItems.length > 0 && (
            <Box display="flex" flexDirection="column" gap={2}>
              {bidLineItems.map((lineItem) => (
                <BidLineItemCard
                  key={lineItem.id}
                  item={lineItem}
                  disabled={isLoading || openLineItemForm}
                  handleEdit={(item) => {
                    setOpenLineItemForm(true);
                    reset({
                      ...item,
                      categoryIds: item.categories.map((c) => c.id),
                      accountId: item.account.id || accountDetails?.id || "",
                    });
                  }}
                  handleDelete={(item) => {
                    setSelectedLineItem(item);
                    setOpenDeleteModal(true);
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </ConfigAccordionHeader>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDeleteModal}
        description={`You are about to delete ${selectedLineItem?.name}. Are you sure you would like to continue?`}
      />
    </>
  );
}
